import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import {
  fetchIndividuals,
  setPage,
  setPageSize,
  setName,
  setService,
  setStatus,
  setSortKey,
  removeIndividual,
  setInviteOpen,
  sendIndividualInvite,
  setEditingModalOpen,
  setEditingIndividual,
  updateIndividual,
} from "./individuals.slice";
import { uuid } from "@/interfaces";
import { toast } from "sonner";
import type { SettableWorkerContractStatus } from "@shyne/shared/api";
import type { WorkerContractSortKey } from "@/repositories/individuals.repository";
import { IndividualWithJoin } from "./individuals.entity";

export function useIndividuals() {
  const dispatch = useAppDispatch();
  const { 
    individuals, 
    loading, 
    total, 
    filters, 
    inviteSaving, 
    inviteOpen, 
    inviteError,
    editingModalOpen,
    editingIndividual 
  } = useAppSelector((s) => s.individuals);


  useEffect(() => {
    dispatch(fetchIndividuals());
  }, [
    filters.page,
    filters.pageSize,
    filters.name,
    filters.service,
    filters.status,
    filters.sortKey,
    filters.sortOrder,
    dispatch,
  ]);

  const unlinkIndividual = async (individualId: uuid) => {
    await dispatch(removeIndividual(individualId));
    dispatch(fetchIndividuals());
  };

  const sendInvite = async (email: string) => {
    const res = await dispatch(sendIndividualInvite(email)).unwrap();

    if (res.alreadyOnboarded) {
      toast.success("Individual has been linked successfully!");
      dispatch(fetchIndividuals());
    } else {
      toast.success("Individual has been sent an invite!");
    }

    openInviteModal(false);

    return res;
  };

  const openInviteModal = (open: boolean) => dispatch(setInviteOpen(open));

  const handleEditIndividual = (individual: IndividualWithJoin) => {
    dispatch(setEditingIndividual(individual));
    dispatch(setEditingModalOpen(true));
  };

  const saveIndividualStatus = async (status: SettableWorkerContractStatus) => {
    if (!editingIndividual) return;

    await dispatch(updateIndividual({ 
      orgIndividualId: editingIndividual.id, 
      status 
    })).unwrap();
    
    dispatch(fetchIndividuals());
    dispatch(setEditingIndividual(null));
    dispatch(setEditingModalOpen(false));
    
    toast.success("Individual status updated successfully.");
  };

  const setEditingModalOpenState = (open: boolean) => {
    dispatch(setEditingModalOpen(open));
    if (!open) dispatch(setEditingIndividual(null));
  };

  return {
    individuals,
    loading,
    total,
    page: filters.page,
    pageSize: filters.pageSize,
    name: filters.name,
    service: filters.service,
    status: filters.status,
    sortKey: filters.sortKey,
    sortOrder: filters.sortOrder,
    setPage: (p: number) => dispatch(setPage(p)),
    setPageSize: (s: number) => dispatch(setPageSize(s)),
    setName: (n: string) => dispatch(setName(n)),
    setService: (svc: string) => dispatch(setService(svc)),
    setStatus: (st: string) => dispatch(setStatus(st)),
    setSortKey: (key: WorkerContractSortKey) => dispatch(setSortKey(key)),
    removeIndividual: unlinkIndividual,

    inviteOpen,
    inviteError,
    inviteSaving,
    openInviteModal,
    sendInvite,

    editingModalOpen,
    editingIndividual,
    handleEditIndividual,
    saveIndividualStatus,
    setEditingModalOpen: setEditingModalOpenState,
  };
}